import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { Delete, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { Drawer } from '~/component/common/Dialog';
import FileUploader from '~/component/common/FileUploader';
import { parseCSVFile, convertCSVToSurveyRequests, validateSurveyData } from '../../utils/csvParser';
import { ServerCreateSurveyRequest, ServerBulkCreateSurveysResponse, RpcStatus, HttpResponse } from '../../myApi';
import { createMessages } from '~/const/message';

const Container = styled.div`
  padding: 24px;
`;

const StepperContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const FileInfoBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: #f0f0f0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FileNameText = styled(Typography)`
  flex: 1;
`;

const SurveyCountTitle = styled(Typography)`
  margin-bottom: 16px;
`;

const ResultContainer = styled.div`
  text-align: center;
  margin-top: 24px;
`;

const ResultIcon = styled.div<{ success: boolean }>`
  svg {
    font-size: 64px;
    color: ${props => props.success ? '#4caf50' : '#f44336'};
    margin-bottom: 16px;
  }
`;

const ResultText = styled(Typography) <{ success: boolean }>`
  color: ${props => props.success ? '#4caf50' : '#f44336'};
`;

interface BulkUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (surveys: ServerCreateSurveyRequest[]) => Promise<HttpResponse<ServerBulkCreateSurveysResponse, RpcStatus>>;
}

const BulkUploadDialog: React.FC<BulkUploadDialogProps> = ({ open, onClose, onUpload }) => {
  const messages = createMessages();
  const [file, setFile] = useState<File | null>(null);
  const [surveys, setSurveys] = useState<ServerCreateSurveyRequest[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });

  const steps = ['ファイル選択', 'データ確認', 'アップロード'];

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      messages.error('CSVファイルを選択してください');
      return;
    }

    setFile(selectedFile);
    setLoading(true);
    setErrors([]);

    try {
      const csvData = await parseCSVFile(selectedFile);
      const surveyRequests = convertCSVToSurveyRequests(csvData);
      const validation = validateSurveyData(surveyRequests);

      console.log(csvData);
      console.log(surveyRequests);
      console.log(validation);

      if (!validation.isValid) {
        setErrors(validation.errors);
        messages.error('データにエラーがあります。修正してください。');
      } else {
        setSurveys(surveyRequests);
        setActiveStep(1);
        messages.success(`${surveyRequests.length}件のサーベイを読み込みました`);
      }
    } catch (error) {
      console.error('CSV parsing error:', error);
      messages.error('CSVファイルの読み込みに失敗しました');
      setErrors([error instanceof Error ? error.message : 'Unknown error']);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    setLoading(true);
    setActiveStep(2);
    setUploadProgress(0);
    setUploadResults({ success: 0, failed: 0 });

    try {
      const response = await onUpload(surveys);
      if (response.ok) {
        setUploadResults({ success: surveys.length, failed: 0 });
        messages.createSuccess(`${surveys.length}件のサーベイ`);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        console.error('Upload error:', response.error);
        setUploadResults({ success: 0, failed: surveys.length });
        messages.error(response.error?.message || 'アップロードに失敗しました');
      }
    } catch (error) {
      setUploadResults({ success: 0, failed: surveys.length });

      let errorMessage = 'アップロードに失敗しました';
      if (error && typeof error === 'object' && 'error' in error) {
        const apiError = error as { error: RpcStatus };
        errorMessage = apiError.error.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      messages.error(errorMessage);
    } finally {
      setLoading(false);
      setUploadProgress(100);
    }
  };

  const handleClose = () => {
    setFile(null);
    setSurveys([]);
    setErrors([]);
    setActiveStep(0);
    setUploadProgress(0);
    setUploadResults({ success: 0, failed: 0 });
    onClose();
  };


  const renderDrawerButtons = () => {
    if (activeStep === 0) {
      return null;
    } else if (activeStep === 1) {
      return (
        <>
          <Button onClick={handleClose} variant="text">
            キャンセル
          </Button>
          <Button
            onClick={() => {
              setActiveStep(0);
              setFile(null);
              setSurveys([]);
              setErrors([]);
            }}
            variant="outlined"
          >
            戻る
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={loading || surveys.length === 0}
          >
            アップロード実行
          </Button>
        </>
      );
    } else {
      return (
        <Button onClick={handleClose} variant="contained">
          閉じる
        </Button>
      );
    }
  };

  return (
    <Drawer
      isOpen={open}
      onClose={handleClose}
      title="サーベイ一括アップロード"
      footerButtons={renderDrawerButtons()}
    >
      <Container>
        <StepperContainer>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </StepperContainer>

        {activeStep === 0 && (
          <div>
            <FileUploader
              accept=".csv"
              onChange={handleFileSelect}
              label="ここをクリックしてCSVファイルを選択"
              subLabel="またはドラッグ&ドロップ"
              disabled={loading}
            />

            {file && (
              <FileInfoBox>
                <FileNameText variant="body2">
                  選択されたファイル: {file.name}
                </FileNameText>
                <IconButton size="small" onClick={() => setFile(null)}>
                  <Delete fontSize="small" />
                </IconButton>
              </FileInfoBox>
            )}

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.map((error, index) => (
                  <Typography key={index} variant="body2">{error}</Typography>
                ))}
              </Alert>
            )}
          </div>
        )}

        {activeStep === 1 && (
          <div>
            <SurveyCountTitle variant="h6">
              アップロード予定のサーベイ: {surveys.length}件
            </SurveyCountTitle>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>サーベイ名</TableCell>
                    <TableCell>配信期間</TableCell>
                    <TableCell>目標回答数</TableCell>
                    <TableCell>質問数</TableCell>
                    <TableCell>タイプ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveys.map((survey, index) => (
                    <TableRow key={index}>
                      <TableCell>{survey.name}</TableCell>
                      <TableCell>
                        {new Date(Number(survey.surveyStartAt) * 1000).toLocaleDateString('ja-JP')} 〜
                        {new Date(Number(survey.surveyEndAt) * 1000).toLocaleDateString('ja-JP')}
                      </TableCell>
                      <TableCell>{survey.targetCollectionNum}</TableCell>
                      <TableCell>{survey.questions?.length || 0}</TableCell>
                      <TableCell>{survey.type === 'SURVEY_TYPE_AD_CONTACT' ? '接触調査' : '事前調査'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        {activeStep === 2 && (
          <div>
            <SurveyCountTitle variant="h6">
              アップロード中...
            </SurveyCountTitle>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />

            {uploadProgress === 100 && (
              <ResultContainer>
                {uploadResults.failed === 0 ? (
                  <>
                    <ResultIcon success={true}>
                      <CheckCircle />
                    </ResultIcon>
                    <ResultText variant="h6" success={true}>
                      {uploadResults.success}件のサーベイを正常に作成しました
                    </ResultText>
                  </>
                ) : (
                  <>
                    <ResultIcon success={false}>
                      <ErrorIcon />
                    </ResultIcon>
                    <ResultText variant="h6" success={false}>
                      アップロードに失敗しました
                    </ResultText>
                  </>
                )}
              </ResultContainer>
            )}
          </div>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Container>
    </Drawer>
  );
};

export default BulkUploadDialog;